import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_URL } from 'app/helpers/globals';
import { delay, map, share, shareReplay } from 'rxjs/operators';
import {
    CompaniesResponse,
    Company,
    CompanyFlagsResponse,
    CompanyInfo,
    CompanyInfoResponse,
    CompanyInfoUpdateRequest,
    EmailNotification,
    EmailNotificationSettingsResponse,
    ReportContact,
    ReportContactResponse,
    ReportFilesReponse,
    ReportStatisticsResponse
} from './company.model';
import { ProService } from '../pro/pro.service';
import { ViewAsTestService } from '../view-as-test.service';
import { SettingsService } from '../settings/settings.service';
import { overrideMomentTimezone } from 'app/moment.override';
import { FleetScoreResponse } from 'app/views/analysis/fleet-score/fleet-score.model';

@Injectable()
export class CompanyService {

    private subcompaniesCache: { observable: Observable<Company[]>, timestamp: number, userId: number } | null = null;
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

    constructor(private http: HttpClient,
        private readonly proService: ProService,
        private readonly viewAsTestService: ViewAsTestService,
        private readonly settingsService: SettingsService
    ) {
    }

    private getLoggedInUserId(): number {
        try {
            const user = JSON.parse(localStorage.getItem('loggedInUser'));
            return user?.id || 0;
        } catch {
            return 0;
        }
    }

    getAllSubcompanies(): Observable<Company[]> {
        const now = Date.now();
        const currentUserId = this.getLoggedInUserId();

        if (this.subcompaniesCache
            && this.subcompaniesCache.userId === currentUserId
            && (now - this.subcompaniesCache.timestamp) < this.CACHE_DURATION) {
            return this.subcompaniesCache.observable.pipe(delay(100));
        }

        const request$ = this.http.get<CompaniesResponse>(API_URL + 'company/list', { withCredentials: true })
            .pipe(
                map((response) => response.companies),
                shareReplay({ bufferSize: 1, refCount: false })
            );

        this.subcompaniesCache = { observable: request$, timestamp: now, userId: currentUserId };

        return request$;
    }

    getCompanyInfo(clientId: number): Observable<CompanyInfo> {
        return this.http.get<CompanyInfoResponse>(API_URL + `company/getCompanyInfo?clientId=${clientId}`, { withCredentials: true })
            .pipe(share(), map((response) => response.company));
    }

    updateCompanyInfo(payload: CompanyInfoUpdateRequest): Observable<CompanyInfo> {
        return this.http.post<CompanyInfoResponse>(API_URL + 'company/updateCompanyInfo', payload, { withCredentials: true })
            .pipe(share(), map((response) => response.company));
    }

    getClientReportContact(clientId: number): Observable<ReportContact> {
        return this.http.get<ReportContactResponse>(API_URL + `company/getClientReportContact?clientId=${clientId}`, { withCredentials: true })
            .pipe(share(), map((response) => {
                if (response.result === 'SUCCESS') {
                    return response;
                } else {
                    return {
                        reportLogo: '',
                        reportContactEmail: '',
                        reportContactPhone: ''
                    }
                }
            }));
    }

    getCorruptedFilesSettings(clientId: number): Observable<{ saveCorruptedFiles: boolean, processCorruptedFiles: boolean }> {
        return this.http.get<{ msg: string, saveCorruptedFiles: boolean, processCorruptedFiles: boolean }>(
            API_URL + `company/getCorruptedFilesSettings?clientId=${clientId}`,
            { withCredentials: true }
        ).pipe(
            share(),
            map((response) => ({
                saveCorruptedFiles: response.saveCorruptedFiles,
                processCorruptedFiles: response.processCorruptedFiles
            }))
        );
    }

    saveCorruptedFilesSettings(
        clientId: number,
        saveCorruptedFiles: boolean,
        processCorruptedFiles: boolean,
        forAll: boolean
    ): Observable<{ msg: string }> {
        return this.http.post<{ msg: string }>(
            API_URL + 'company/saveCorruptedFilesSettings',
            {
                clientId,
                saveCorruptedFiles,
                processCorruptedFiles,
                forAll
            },
            { withCredentials: true }
        ).pipe(share());
    }

    getFleetScoreOverview(
        clientId: number,
        startDate: number,
        endDate: number,
        includeSummary = false,
        language?: string
    ): Observable<FleetScoreResponse> {
        return this.http.post<FleetScoreResponse>(
            API_URL + 'fleetScore/overview',
            { clientId, startDate, endDate, includeSummary, language },
            { withCredentials: true }
        ).pipe(share());
    }

    getMissingMileageMinLimit(clientId: number): Observable<{ missingMileageMinLimit: number }> {
        const params = new HttpParams().set('clientId', clientId.toString());
        return this.http.post<{ missingMileageMinLimit: number, msg: string }>(
            API_URL + 'company/getMissingMileageMinLimit',
            null,
            { params, withCredentials: true }
        ).pipe(
            share(),
            map(response => ({ missingMileageMinLimit: response.missingMileageMinLimit }))
        );
    }

    setMissingMileageMinLimit(clientId: number, minLimit: number, forAll?: boolean): Observable<{ msg: string }> {
        let params = new HttpParams()
            .set('clientId', clientId.toString())
            .set('minLimit', minLimit.toString());
        if (forAll) {
            params = params.set('forAll', 'true');
        }
        return this.http.post<{ msg: string }>(
            API_URL + 'company/setMissingMileageMinLimit',
            null,
            { params, withCredentials: true }
        ).pipe(share());
    }

    getEmailNotificationSettings(clientId: number): Observable<EmailNotification[]> {
        return this.http.get<EmailNotificationSettingsResponse>(
            API_URL + `company/getEmailNotificationSettings?clientId=${clientId}`,
            { withCredentials: true }
        ).pipe(
            share(),
            map(response => response?.data?.clientNotificationSettings || [])
        );
    }

    setEmailNotifications(clientId: number, notifications: EmailNotification[], forAll: boolean): Observable<{ msg: string }> {
        return this.http.post<{ msg: string }>(
            API_URL + `company/setEmailNotifications?forAll=${forAll || false}`,
            { clientId, notifications },
            { withCredentials: true }
        ).pipe(share());
    }

    registerReminderEmail(clientId: number, email: string, forAll?: boolean): Observable<{ result: string, message: string }> {
        const params = new HttpParams()
            .set('forAll', (forAll || false).toString())
            .set('clientId', clientId.toString())
            .set('email', email);
        return this.http.post<{ result: string, message: string }>(
            API_URL + 'company/registerReminderEmail',
            null,
            { params, withCredentials: true }
        ).pipe(share());
    }

    getReminderEmail(clientId: number): Observable<string> {
        return this.http.get<{ reminderEmail: string }>(
            API_URL + `company/getReminderEmail?clientId=${clientId}`,
            { withCredentials: true }
        ).pipe(
            share(),
            map(response => response?.reminderEmail || null)
        );
    }

    getStatisticsForReports(clientId: number) {
        return this.http.get<ReportStatisticsResponse>(API_URL + 'getStatisticsForReports/' + clientId,
            { withCredentials: true }).pipe(share(), map(response => {
                return response;
            }));
    }

    getReportFiles(clientId: number) {
        return this.http.get<ReportFilesReponse>(API_URL + 'company/getAllFiles/' + clientId,
            { withCredentials: true }).pipe(share(), map(response => {
                return response;
            }));
    }

    getCompanyFlags(clientId: number) {
        try {
            this.settingsService.getLanguageAndTimezoneByClient(clientId).subscribe(
                res => {
                    overrideMomentTimezone(res.timezone);
                }
            );
        } catch (e) { }
        return this.http.get<CompanyFlagsResponse>(API_URL + 'company/getCompanyFlags/' + clientId, { withCredentials: true })
            .pipe(share(), map(response => {
                const loggedInUser = JSON.parse(localStorage.getItem('loggedInUser'));
                const forcePro = loggedInUser.clientId === 21 && loggedInUser.userClass !== 4;
                localStorage.setItem('isPro', ['REQUESTED', 'TRIAL_PRO', 'TRIAL'].includes(response.PROType) ? 'true' : 'false');
                this.viewAsTestService.setTypeOfAccount(response.typeOfAccount);
                this.proService.setPro({
                    // pro:'NOT_PRO'
                    // pro: 'REQUESTED'
                    pro: forcePro ? 'REQUESTED' : (response.PROType === 'PENDING' ? 'NOT_PRO' : (response.PROType === 'TRIAL_PRO' ? 'REQUESTED' : response.PROType)),
                    showBanner: response.PROType === 'TRIAL' || response.PROType === 'TRIAL_PRO'
                });
                return response;
            }));
    }

    postCompanyFlags(clientId: number,
        expenseClient: boolean,
        nfcClient: boolean,
        connectedClient: boolean,
        uploaderClient: boolean,
        analyserClient: boolean) {
        return this.http.post<CompanyFlagsResponse>(API_URL + 'company/setCompanyFlags/' + clientId,
            {
                expenseClient: expenseClient,
                nfcClient: nfcClient,
                connectedClient: connectedClient,
                uploaderClient: uploaderClient,
                analyserClient: analyserClient
            }, { withCredentials: true }).pipe(share(), map(response => {
                return response;
            }));
    }

    downloadFilesPerClient(clientId: number, fileType: 1 | 2, periodStart: number, periodEnd: number) {
        return this.http.get(`${API_URL}downloadFilesPerClient?clientId=${clientId}&fileType=${fileType}&format=&periodStart=${periodStart}&periodEnd=${periodEnd}`, { withCredentials: true, responseType: 'blob', })
            .pipe(share(), map((response) => {

                // const blob = new Blob([response], { type: 'application/zip' });
                // const url = window.URL.createObjectURL(blob);
                // const a = document.createElement('a');
                // a.href = url;
                // a.download = `files_${fileType}_${periodStart}_${periodEnd}.zip`;
                // document.body.appendChild(a);
                // a.click();
                // window.URL.revokeObjectURL(url);
                // a.remove();
                return response;
            }))
    }

    downloadFilesForTheAuthorities(clientId: number, periodStart: number, periodEnd: number, vins: string[]) {
        return this.http.post(`${API_URL}downloadFilesForTheAuthorities`, {
            clientId: clientId,
            periodStart: periodStart,
            periodEnd: periodEnd,
            vins: vins
        }, {
            withCredentials: true,
            responseType: 'blob',
        })
            .pipe(share(), map((response) => {
                return response;
            }))
    }
}
